import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import { getInvoicesApi } from '@api/billing-publisher.api';
import ConfigurableTable from '@uikit/old/ConfigurableTable/ConfigurableTable';
import Pagination from '@uikit/old/Pagination/Pagination';
import usePagination from '@hooks/usePagination';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';

import { BillingPublisherInvoiceType } from '@ts/billing-publisher.types';

import { mapInvoiceStatus } from '../../constants';
import { useInvoicePdfDownload } from '../../hooks';
import { formatUsd } from '../../utils';

import { invoicesTableConfig, InvoicesTableRenderProps } from './tableConfig';

function InvoicesTable(): JSX.Element {
  const workspaceId = useCurrentWorkspaceId();
  const { page, changePage, limit, changeLimit } = usePagination();
  const { activeId, download } = useInvoicePdfDownload(workspaceId);

  const { data: invoicesData = [] } = useQuery<BillingPublisherInvoiceType[]>(
    ['invoices-list', page, limit],
    () => getInvoicesApi(page - 1, limit, ''),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchInterval: 30 * 60 * 1000,
    }
  );

  const rows = useMemo(
    () =>
      invoicesData.map((inv) => ({
        ...inv,
        statusFormatted: mapInvoiceStatus(inv.status),
        amountFormatted: formatUsd(inv.amount),
      })),
    [invoicesData]
  );

  const renderProps: InvoicesTableRenderProps = {
    onDownload: download,
    activeDownloadId: activeId,
  };

  return (
    <>
      <ConfigurableTable
        data={rows}
        config={invoicesTableConfig}
        renderProps={renderProps}
        testId="publisher-invoices-table"
        rowTestId={(row) => `publisher-invoices-row-${row.id}`}
      />
      <Pagination
        curPage={page}
        onChangePage={changePage}
        pageLimit={limit}
        onChangePageLimit={changeLimit}
        totalItems={invoicesData[0]?.totalItems}
      />
    </>
  );
}

export default InvoicesTable;
